import moment from 'moment'
import { OutletListItem } from '@ancon/wildcat-types'
import getTimeSpanParts from '@ancon/wildcat-utils/time/getTimeSpanParts'

type OutletTimeZone = Pick<OutletListItem, 'timeZoneId' | 'timeZoneOffset'>

function getDateInTimeZone(timeZoneId: string, now: Date) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now)

    const getPart = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find(part => part.type === type)?.value

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`
  } catch {
    // Not a time zone the browser knows
    return null
  }
}

/** The outlet's UTC offset in minutes from its time span, e.g. "-05:30:00" */
function getOffsetMinutes(timeZoneOffset: string) {
  const isNegative = timeZoneOffset.trim().startsWith('-')
  const { hours, minutes } = getTimeSpanParts(
    timeZoneOffset.trim().replace(/^[+-]/, ''),
  )
  const offset = Math.abs(hours) * 60 + Math.abs(minutes)

  return Number.isNaN(offset) ? null : (isNegative ? -1 : 1) * offset
}

/**
 * Today at the outlet, as the start of that calendar day: from its time zone,
 * else its UTC offset, else the browser's day, e.g. before a restaurant is
 * chosen
 */
export default function getOutletToday(
  outlet?: OutletTimeZone | null,
  now: Date = new Date(),
) {
  const zoneDate = outlet?.timeZoneId
    ? getDateInTimeZone(outlet.timeZoneId, now)
    : null

  if (zoneDate) {
    return moment(zoneDate, 'YYYY-MM-DD')
  }

  const offsetMinutes = outlet?.timeZoneOffset
    ? getOffsetMinutes(outlet.timeZoneOffset)
    : null

  if (offsetMinutes !== null) {
    const date = moment(now).utcOffset(offsetMinutes).format('YYYY-MM-DD')

    return moment(date, 'YYYY-MM-DD')
  }

  return moment(now).startOf('day')
}
